import { ShiftTiming } from 'Interfaces/ReferenceInterfaces/ShiftTimingsInterfaces';
import { useEffect, useRef, useState } from 'react';
import { shiftTimingStore } from 'stores/ReferenceStore';
import { useInterval } from 'hooks/usePolling';
import { getJitteredInterval } from 'utils/pollingUtility';
import {toast} from 'react-toastify';

interface FormData {
  shift_name: string;
  start_time: string;
  end_time: string;
}

export const useShiftTimingTable = (pageSize: number = 100) => {
  const [selectedRow, setSelectedRow] = useState<ShiftTiming | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemMenu = useRef<any>(null);
  const environment = import.meta.env.VITE_ENVIRONMENT;
  const POLLING_INTERVAL = getJitteredInterval();

  useEffect(() => {
    shiftTimingStore.fetchShiftTimings(currentPage, pageSize);
  }, [currentPage, pageSize]);

  // Set up polling for data updates
  useInterval(async () => {
    try {
      await shiftTimingStore.pollShiftTimings(currentPage, pageSize);
    } catch (error) {
      // Don't break the polling cycle on error
      if (environment === 'development') {
        console.error('Error during polling:', error);
      }
      toast.error('Error during polling for Shift Timings data!');
    }
  }, POLLING_INTERVAL);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleAddRow = async (formData: FormData): Promise<void> => {
    if (selectedRow) {
      // Edit existing row
      const success = await shiftTimingStore.updateShiftTiming(selectedRow.id, {
        shift_name: formData.shift_name,
        start_time: formData.start_time,
        end_time: formData.end_time
      });
      if (!success) {
        throw new Error('Failed to update shift timing');
      }
    } else {
      // Add new row
      const success = await shiftTimingStore.createShiftTiming({
        shift_name: formData.shift_name,
        start_time: formData.start_time,
        end_time: formData.end_time,
        flag: true
      });
      if (!success) {
        throw new Error('Failed to create shift timing');
      }
    }
    setSelectedRow(null);
  };

  const handleDeleteRow = () => {
    if (selectedRow) {
      shiftTimingStore.deleteShiftTiming(selectedRow.id);
      setSelectedRow(null);
      setShowDeleteModal(false);
    }
  };

  const prepareFormData = (row: ShiftTiming): FormData => {
    return {
      shift_name: row.shift_name || '',
      start_time: row.start_time || '',
      end_time: row.end_time || ''
    };
  };

  return {
    shiftTimingData: shiftTimingStore.shiftTimings,
    isLoading: shiftTimingStore.isLoading,
    pagination: shiftTimingStore.pagination,
    itemMenu,
    selectedRow,
    showAddModal,
    showDeleteModal,
    setSelectedRow,
    setShowAddModal,
    setShowDeleteModal,
    handleAddRow,
    handleDeleteRow,
    handlePageChange,
    prepareFormData
  };
};
